import { FC, ReactElement } from 'react';

import { AccountData } from '../../containers';
import { Customer } from '../../entities/@account/Customer/Customer';
import { Address } from '../../entities/Address/Address';
import { areObjectsEqual } from '../../helpers/object';
import { authenticateUser } from '../../redux/@account/authentication/authenticationActions';
import {
    addAddress,
    updateCustomer,
    updateCustomerAddress,
    updateDefaultAddress,
} from '../../redux/@account/customer/customerActions';
import { useTypedDispatch, useTypedSelector } from '../../redux/store';

interface ConnectedAccountDataProps {
    className?: string;
}

export const ConnectedAccountData: FC<ConnectedAccountDataProps> = ({
    className = '',
}): ReactElement | null => {
    const dispatch = useTypedDispatch();

    const isSuccessful = useTypedSelector(state => state.customerSlice.isSuccessful);
    const customer = useTypedSelector(state => state.customerSlice.customer);

    if (!customer) {
        return null;
    }

    const handleUpdateCustomer = async (newCustomerData: Customer): Promise<void> => {
        const { password, ...body } = newCustomerData;
        const updatedCustomer = { ...customer, ...newCustomerData };

        const hasChanges = !areObjectsEqual(customer, body);
        if (!hasChanges) {
            return;
        }

        await dispatch(updateCustomer(body));

        const emailChanged = updatedCustomer.email !== customer?.email;
        if (emailChanged && password) {
            await dispatch(authenticateUser({
                email: updatedCustomer.email,
                password,
                rememberMe: true,
            }));
        }
    };

    const handleUpdateAddresses = (newAddress: Address): void => {
        const matchingAddress = customer.addresses.find(address => address.id === newAddress.id);

        if (matchingAddress) {
            const addressHasActualChanges = !areObjectsEqual(matchingAddress, newAddress);

            if (addressHasActualChanges) {
                dispatch(updateCustomerAddress(newAddress));
            }

            return;
        }

        dispatch(addAddress(newAddress));
    };

    const setDefaultAddress = (id: string): void => {
        dispatch(updateDefaultAddress(id));
    };

    return (
        <AccountData
            successfullyAddedAddress={isSuccessful}
            customer={customer}
            onUpdateCustomer={handleUpdateCustomer}
            onUpdateAddresses={handleUpdateAddresses}
            onChangeShippingAddress={setDefaultAddress}
            className={className}
        />
    );
};
